import { Stack } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import DaysLookBackSummary from './7DaysLookBack';
import { neutral } from 'src/theme/colors';

function getLast6thDate(date: string): string {
  return moment(date).subtract(6, 'days').format('YYYY-MM-DD');
}

const EMPTY_DATE = new Date();

const DEFAULT_TOTAL_MINS = Array.from({ length: 9 }, () => ({
  totalPlanMinutes: '',
  totalActualMinutes: '',
  date: EMPTY_DATE,
}));

const DEFAULT_VARIATION = Array.from({ length: 9 }, () => ({
  variation: '',
  rugsScore: '',
  date: EMPTY_DATE,
}));

const EMPTY_TOTAL_MINS_CELLS = Array.from({ length: 9 }, () => ({
  totalPlanMinutes: '',
  totalActualMinutes: '',
}));

const EMPTY_VARIATION_CELLS = Array.from({ length: 9 }, () => ({
  variation: '',
  rugsScore: '',
}));

const sortByDate = (a: any, b: any): number =>
  new Date(a.date).getTime() - new Date(b.date).getTime();

const labelCellSx = {
  height: '33%',
  justifyContent: 'center',
  paddingLeft: '10px',
  backgroundColor: '#F8F9FA',
  borderRight: '1px solid #E5E7EB',
  fontSize: '13px',
  fontWeight: '500',
  color: neutral[900],
};

const getDataCellSx = (isToday: boolean, emptyBg = '#FFF') => ({
  backgroundColor: isToday ? '#F5F7FF' : emptyBg,
  borderRight: '1px solid #E5E7EB',
  width: 'calc(100% / 7)',
  fontSize: '14px',
  fontWeight: '400',
  color: '#111927',
});

const ROW_LABELS = [
  { label: 'Daily Total Min.', extraSx: { borderTop: '1px solid #E5E7EB' } },
  { label: 'RUGs Total', extraSx: {} },
  { label: 'Variation', extraSx: {} },
];

const SummaryCard_2_0 = ({
  totalMins = DEFAULT_TOTAL_MINS,
  variation = DEFAULT_VARIATION,
  currentsWeekList = [],
  lookBackSum,
}: any) => {
  const totalMinsResponse = totalMins?.sort(sortByDate);
  const variationResponse = variation?.sort(sortByDate);

  const isTodayDate = currentsWeekList?.findIndex(
    (date: any) => date === moment().format('YYYY-MM-DD')
  );

  const [copyTemplateStartDate, setCopyTemplateStartDate] = useState(null);

  const getSevenDayBack = (currentDate: any) => {
    if (!copyTemplateStartDate) return false;
    return (
      currentDate <= copyTemplateStartDate && currentDate >= getLast6thDate(copyTemplateStartDate)
    );
  };

  return (
    <Stack
      sx={{ width: '100%', height: '120px' }}
      flexDirection="row"
    >
      <Stack
        sx={{ height: '100%' }}
        flex={1.5}
      >
        {ROW_LABELS.map(({ label, extraSx }) => (
          <Stack
            key={label}
            sx={{ ...labelCellSx, ...extraSx }}
          >
            {label}
          </Stack>
        ))}
      </Stack>
      <Stack
        sx={{ height: '100%' }}
        flex={8.5}
      >
        <Stack
          sx={{ width: '100%', height: '33%' }}
          flexDirection="row"
        >
          {(totalMinsResponse?.length > 0 ? totalMinsResponse : EMPTY_TOTAL_MINS_CELLS)?.map(
            (min: any, index: number) => (
              <Stack
                key={index}
                alignItems="center"
                justifyContent="center"
                sx={getDataCellSx(isTodayDate === index)}
              >
                {min?.totalPlanMinutes === '' && min?.totalActualMinutes === ''
                  ? new Date(currentsWeekList[index]) < new Date() || isTodayDate === index
                    ? `0 | 0`
                    : `0`
                  : new Date(currentsWeekList[index]) < new Date() && isTodayDate !== index
                  ? min?.totalPlanMinutes != 0 || min?.totalActualMinutes != 0
                    ? `${min?.totalPlanMinutes} | ${min?.totalActualMinutes}`
                    : `0 | 0`
                  : `${min?.totalPlanMinutes} | ${min?.totalActualMinutes}`}
              </Stack>
            )
          )}
        </Stack>
        <Stack
          sx={{ width: '100%', height: '33%', backgroundColor: '#FFF' }}
          flexDirection="row"
        >
          {lookBackSum?.map((sumItem: any, index: number) => {
            const isHighlighted = getSevenDayBack(sumItem?.date);
            return (
              <Stack
                alignItems="center"
                key={index}
                justifyContent="center"
                sx={{
                  ...getDataCellSx(isTodayDate === index),
                  backgroundColor: isHighlighted
                    ? 'rgba(177, 178, 248, 0.15)'
                    : isTodayDate === index
                    ? '#F5F7FF'
                    : '#FFF',
                  fontWeight: isHighlighted ? '800' : '500',
                  color: isHighlighted ? '#6366F1' : neutral[900],
                  fontSize: '14px',
                }}
                onMouseEnter={() => setCopyTemplateStartDate(sumItem?.date)}
                onMouseLeave={() => setCopyTemplateStartDate(null)}
              >
                {sumItem?.lookbackSum ? sumItem?.lookbackSum : 0}
              </Stack>
            );
          })}
        </Stack>
        <Stack
          sx={{ width: '100%', height: '33%', backgroundColor: '#FFF' }}
          flexDirection="row"
        >
          {(variationResponse?.length > 0 ? variationResponse : EMPTY_VARIATION_CELLS).map(
            (item: any, index: number) => (
              <Stack
                key={index}
                alignItems="center"
                justifyContent="center"
                sx={getDataCellSx(
                  isTodayDate === index,
                  variationResponse?.length > 0 ? '#FFF' : '#F3F4F6'
                )}
              >
                {item?.variation === '' ? '0' : item?.variation}
              </Stack>
            )
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SummaryCard_2_0;
